import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { trackMetaEvent } from '@/utils/metaPixel';
import { useTurnRecorder } from '@/hooks/useTurnRecorder';
import { VoiceDiagnosticsPanel } from '@/components/VoiceDiagnosticsPanel';
import { createConversationMachine, VOICE_STATES } from '@/voice/conversationMachine';
import { createOrderedChunkBuffer } from '@/voice/orderedChunkBuffer';
import { createSseParser } from '@/voice/sseParser';
import { verifyStreamIntegrity } from '@/voice/responseIntegrity';
import { runVoiceSelfTests } from '@/voice/voiceSelfTests';
import { createLatencyWaterfall, markLatencyStage, serializeLatencyWaterfall, summarizeLatencyWaterfall } from '@/voice/latencyWaterfall';
import { 
  Mic, 
  Video, 
  VideoOff, 
  Square,
  Pause,
  Play,
  MessageSquare,
  RefreshCw,
  ArrowRight,
  ArrowLeft,
  X,
  Volume2,
  VolumeX,
  Clock,
  Lightbulb,
  ChevronRight,
  Send,
  AlertTriangle,
  Zap,
  Target,
  Shield,
  Check,
  TrendingUp,
  Hand,
  Save,
  Activity
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PracticeRoom() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  
  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(location.state?.scenario || null);
  const [showHints] = useState(location.state?.showHints ?? true);
  const [voiceId] = useState(location.state?.voiceId || '21m00Tcm4TlvDq8ikWAM');
  const [voiceName] = useState(location.state?.voiceName || 'Jerry Citarella');
  const advisorImage = location.state?.voiceImage || '/advisor-rachel.png';
  
  const [isStarted, setIsStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  
  const [messages, setMessages] = useState([]);
  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [speedMode, setSpeedMode] = useState(false);
  
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [currentHint, setCurrentHint] = useState(null);
  const [hintIndex, setHintIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes in seconds
  const [useFallbackInput, setUseFallbackInput] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [pushToTalk, setPushToTalk] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const [complianceAccepted, setComplianceAccepted] = useState(false);
  const [complianceChecked, setComplianceChecked] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [voiceError, setVoiceError] = useState('');
  const [showVoiceDiagnostics, setShowVoiceDiagnostics] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('debugVoice') === 'true' || params.get('voiceDebug') === '1';
  });
  const [conversationState, setConversationState] = useState(VOICE_STATES.IDLE);
  const [currentTurnId, setCurrentTurnId] = useState(null);
  const [processingAcknowledgement, setProcessingAcknowledgement] = useState('');
  const [debugSnapshot, setDebugSnapshot] = useState({ ttsQueue: 0, playbackQueue: 0, chunksReceived: 0, chunksPlayed: 0, chunkOrder: [], memoryUsage: null });
  
  const SESSION_DURATION = 300; // 5 minutes
  
  const videoRef = useRef(null);
  const audioContextRef = useRef(null);
  const messagesEndRef = useRef(null);
  const startTimeRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const safariAudioRef = useRef(null); // Pre-unlocked Audio element for Safari
  const timerRef = useRef(null);
  
  // Refs to track state for callbacks (avoid stale closures)
  const isStartedRef = useRef(false);
  const isPausedRef = useRef(false);
  const isAiSpeakingRef = useRef(false);
  const messagesRef = useRef([]);
  const isEndingRef = useRef(false);
  const isProcessingRef = useRef(false); // Lock to prevent concurrent AI requests
  const autoSaveRef = useRef(null); // Auto-save interval
  const pushToTalkRef = useRef(false); // PTT ref for callbacks
  const audioQueueRef = useRef([]); // Queued audio chunks for gapless playback
  const isPlayingQueueRef = useRef(false); // Is the queue currently playing
  const handleUserSpeechRef = useRef(null);
  const voiceStageHandlerRef = useRef(null);
  const conversationIdRef = useRef(`conversation-${sessionId}-${Date.now()}`);
  const machineRef = useRef(null);
  const activeTurnRef = useRef(null);
  const turnAbortRef = useRef(null);
  const ttsAbortControllersRef = useRef(new Set());
  const orderedBufferRef = useRef(null);
  const requestUserTurnRef = useRef(null);
  const recoveryAttemptsRef = useRef(0);
  const orchestrationTimersRef = useRef(new Set());
  const pendingSessionCompleteRef = useRef(false);
  const memorySamplesRef = useRef([]);
  const pttPointerIdRef = useRef(null);
  const latencyWaterfallsRef = useRef(new Map());
  const markLatencyRef = useRef(null);

  const {
    audioLevel: micLevel,
    diagnostics: voiceDiagnostics,
    environment: voiceEnvironment,
    events: voiceEvents,
    features: voiceFeatures,
    cleanup: cleanupVoice,
    isRecording,
    isTranscribing,
    startTurn,
    stopTurn,
    supportsCapture,
  } = useTurnRecorder({
    apiBase: API,
    token,
    onTranscript: (text, turn) => handleUserSpeechRef.current?.(text, turn),
    onStage: (stage, detail) => voiceStageHandlerRef.current?.(stage, detail),
    onError: (message, code, detail) => {
      setVoiceError(message);
      toast.error(message, { duration: 5000 });
      voiceStageHandlerRef.current?.(code, { status: 'error', error: message, ...detail });
    },
    isTurnCurrent: (turnId) => activeTurnRef.current?.turnId === turnId && !isEndingRef.current,
  });
  const resumeVoiceAudio = () => {};

  if (!machineRef.current) {
    machineRef.current = createConversationMachine({
      conversationId: conversationIdRef.current,
      onTransition: (event) => {
        setConversationState(event.to);
        setCurrentTurnId(event.turnId);
        if (event.to === VOICE_STATES.WAITING_FOR_USER && window.performance?.memory?.usedJSHeapSize) {
          memorySamplesRef.current.push(window.performance.memory.usedJSHeapSize);
          if (memorySamplesRef.current.length > 20) memorySamplesRef.current.shift();
        }
        setDebugSnapshot((current) => ({
          ...current,
          memoryUsage: window.performance?.memory ? {
            usedJSHeapSize: window.performance.memory.usedJSHeapSize,
            totalJSHeapSize: window.performance.memory.totalJSHeapSize,
          } : null,
          memorySamples: [...memorySamplesRef.current],
          memoryGrowth: memorySamplesRef.current.length > 1 ? memorySamplesRef.current.at(-1) - memorySamplesRef.current[0] : 0,
        }));
        axios.post(`${API}/events`, {
          session_id: sessionId,
          type: 'voice_state_transition',
          payload: { ...event, status: 'complete' },
        }, { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }).catch(() => {});
      },
      onIllegalTransition: (event) => {
        console.error('ILLEGAL VOICE TRANSITION', event);
        setVoiceError(`Voice state conflict: ${event.from} cannot transition to ${event.to}.`);
        axios.post(`${API}/events`, {
          session_id: sessionId,
          type: 'voice_illegal_transition',
          payload: { ...event, status: 'error' },
        }, { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }).catch(() => {});
      },
    });
  }

  const transitionVoice = (nextState, metadata = {}) => machineRef.current?.transition(nextState, metadata);
  const setExternalStage = (stage, status, detail = {}) => {
    console.info('VOICE SUBSYSTEM', { stage, status, turnId: activeTurnRef.current?.turnId, ...detail });
    setDebugSnapshot((current) => ({ ...current, [`${stage.toLowerCase()}Status`]: status }));
    axios.post(`${API}/events`, {
      session_id: sessionId,
      type: `voice_${stage.toLowerCase()}`,
      payload: {
        status,
        stage,
        conversation_id: conversationIdRef.current,
        turn_id: activeTurnRef.current?.turnId,
        state: machineRef.current?.getSnapshot().state,
        browser: voiceEnvironment.browser,
        os: voiceEnvironment.os,
        device: voiceEnvironment.device,
        device_model: voiceEnvironment.deviceModel,
        mime_type: detail.mimeType,
        ...detail,
      },
    }, { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }).catch(() => {});
  };

  markLatencyRef.current = (stage, atMs = performance.now(), detail = {}) => {
    const turnId = detail.turn?.turnId || activeTurnRef.current?.turnId;
    if (!turnId) return;
    let waterfall = latencyWaterfallsRef.current.get(turnId);
    if (!waterfall) {
      waterfall = createLatencyWaterfall(turnId);
      latencyWaterfallsRef.current.set(turnId, waterfall);
    }
    markLatencyStage(waterfall, stage, atMs || performance.now(), detail);
    const timeline = serializeLatencyWaterfall(waterfall);
    const summary = summarizeLatencyWaterfall(waterfall);
    setDebugSnapshot((current) => ({ ...current, latencyWaterfall: timeline, latencySummary: summary }));
    console.info('VOICE LATENCY WATERFALL', { turnId, stage, timeline, summary });
    if (stage === 'playback_started') {
      axios.post(`${API}/events`, {
        session_id: sessionId,
        type: 'voice_latency_waterfall',
        payload: { status: summary.regression ? 'error' : 'complete', turn_id: turnId, timeline, ...summary },
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }).catch(() => {});
    }
  };

  const initializeLatencyTurn = (turnId, source) => {
    latencyWaterfallsRef.current.set(turnId, createLatencyWaterfall(turnId));
    setDebugSnapshot((current) => ({
      ...current,
      latencyWaterfall: [],
      latencySummary: { technicalLatencyMs: null, perceivedLatencyMs: null, withinTarget: null, regression: false },
      latencyTurn: { turnId, source, status: 'measuring' },
    }));
  };

  voiceStageHandlerRef.current = (stage, detail = {}) => {
    if (detail.turn?.turnId && detail.turn.turnId !== activeTurnRef.current?.turnId) {
      setExternalStage(stage, 'stale_ignored', { ...detail, staleTurnId: detail.turn.turnId });
      return;
    }
    const state = machineRef.current?.getSnapshot().state;
    const latencyStage = {
      SPEECH_ENDED: 'speech_ended',
      VAD_FIRED: 'vad_fired',
      RECORDING_STOP_REQUESTED: 'recording_stop_requested',
      RECORDING_STOPPED: 'recording_stopped',
      BLOB_FINALIZED: 'blob_finalized',
      UPLOAD_STARTED: 'upload_started',
      UPLOAD_COMPLETED: 'upload_completed',
      STT_STARTED: 'stt_started',
      STT_COMPLETED: 'stt_completed',
    }[stage];
    if (latencyStage) markLatencyRef.current?.(latencyStage, latencyStage === 'speech_ended' ? detail.speechEndedAtMs : detail.clientMonotonicMs, detail);
    if (stage === 'MICROPHONE_REQUESTED') transitionVoice(VOICE_STATES.REQUEST_MICROPHONE, detail);
    else if (stage === 'MICROPHONE_GRANTED') transitionVoice(VOICE_STATES.MICROPHONE_READY, detail);
    else if (stage === 'LISTENING') transitionVoice(VOICE_STATES.LISTENING, detail);
    else if (stage === 'VOICE_DETECTED') {
      transitionVoice(VOICE_STATES.VOICE_DETECTED, detail);
      transitionVoice(VOICE_STATES.RECORDING, detail);
    } else if (stage === 'RECORDING_STOPPED') {
      markLatencyRef.current?.('acknowledged', detail.clientMonotonicMs, { label: 'Got it' });
      if (detail.process) setProcessingAcknowledgement('Got it — thinking…');
      if (detail.process && [VOICE_STATES.RECORDING, VOICE_STATES.VOICE_DETECTED].includes(state)) transitionVoice(VOICE_STATES.PROCESSING_AUDIO, detail);
      else if ([VOICE_STATES.LISTENING, VOICE_STATES.RECORDING, VOICE_STATES.VOICE_DETECTED].includes(state)) {
        transitionVoice(VOICE_STATES.WAITING_FOR_USER, { reason: 'recording_cancelled' });
        activeTurnRef.current = null;
      }
    } else if ((stage === 'BLOB_CREATED' || stage === 'WAITING_FOR_STT') && machineRef.current?.getSnapshot().state === VOICE_STATES.PROCESSING_AUDIO) {
      transitionVoice(VOICE_STATES.WAITING_FOR_STT, detail);
    }
    if (detail.status === 'error') {
      transitionVoice(VOICE_STATES.IDLE, { stage, error: detail.error });
      activeTurnRef.current = null;
      if (!pushToTalkRef.current && isStartedRef.current && !isPausedRef.current && !isEndingRef.current && recoveryAttemptsRef.current < 2) {
        recoveryAttemptsRef.current += 1;
        const timer = setTimeout(() => requestUserTurnRef.current?.({ recovery: true }), 700 * recoveryAttemptsRef.current);
        orchestrationTimersRef.current.add(timer);
      }
    }
    if (stage !== 'AUDIO_FRAME') setExternalStage(stage, detail.status || 'complete', detail);
  };

  // Safari Audio Unlock — must be called during a user gesture (button tap)
  const unlockSafariAudio = () => {
    // Create a persistent Audio element and play a tiny silent mp3 to "unlock" it
    if (!safariAudioRef.current) {
      safariAudioRef.current = new Audio();
    }
    // Tiny silent mp3 (1 frame of silence)
    safariAudioRef.current.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYMhPkEAAAAAAD/+1DEAAAHAAGf9AAAIh2Jqv80AEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVMQU1FMy4xMDBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVQ==';
    safariAudioRef.current.volume = 0.01;
    const playPromise = safariAudioRef.current.play();
    if (playPromise) {
      playPromise.then(() => {
        safariAudioRef.current.pause();
        safariAudioRef.current.currentTime = 0;
        safariAudioRef.current.volume = 1.0;
        console.log('[Safari] Audio element unlocked via user gesture');
      }).catch(e => console.warn('[Safari] Audio unlock failed:', e));
    }
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!audioContextRef.current && AudioContextCtor) {
      audioContextRef.current = new AudioContextCtor();
    }
    // Also unlock AudioContext if it exists
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }
  };

  // Keep Safari audio element "warm" — play silence to maintain recently-played state
  // Called before asynchronous AI work to keep the playback unlock alive
  const keepSafariAudioWarm = () => {
    if (!safariAudioRef.current) return;
    try {
      safariAudioRef.current.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYMhPkEAAAAAAD/+1DEAAAHAAGf9AAAIh2Jqv80AEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVMQU1FMy4xMDBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVQ==';
      safariAudioRef.current.volume = 0.01;
      const p = safariAudioRef.current.play();
      if (p) p.then(() => {
        safariAudioRef.current.pause();
        safariAudioRef.current.currentTime = 0;
        safariAudioRef.current.volume = 1.0;
      }).catch(() => {}); // Silent — this is best-effort
    } catch (e) { /* ignore */ }
  };

  // Keep refs in sync with state
  useEffect(() => { isStartedRef.current = isStarted; }, [isStarted]);
  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);
  useEffect(() => { isAiSpeakingRef.current = isAiSpeaking; }, [isAiSpeaking]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { pushToTalkRef.current = pushToTalk; }, [pushToTalk]);
  useEffect(() => { setIsListening(isRecording); }, [isRecording]);
  useEffect(() => { setUseFallbackInput(!supportsCapture); }, [supportsCapture]);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('debugVoice') === 'true') {
      const results = runVoiceSelfTests();
      console.info('VOICE SELF TESTS', results);
      setDebugSnapshot((current) => ({ ...current, selfTests: results }));
    }
  }, []);

  // Auto-save transcript every 30 seconds
  useEffect(() => {
    if (isStarted && !isEnding) {
      autoSaveRef.current = setInterval(async () => {
        const msgs = messagesRef.current;
        if (msgs.length <= 1) return;
        const fullTranscript = msgs.map(m => `${m.role === 'user' ? 'User' : 'AI Partner'}: ${m.content}`).join('\n');
        const aiTranscript = msgs.filter(m => m.role === 'ai').map(m => m.content).join('\n');
        try {
          await axios.put(`${API}/sessions/${sessionId}`, { transcript: fullTranscript, ai_transcript: aiTranscript }, { headers: { Authorization: `Bearer ${token}` } });
          setAutoSaved(true);
          setTimeout(() => setAutoSaved(false), 2000);
        } catch (e) { /* silent fail */ }
      }, 30000);
    }
    return () => { if (autoSaveRef.current) clearInterval(autoSaveRef.current); };
  }, [isStarted, isEnding, sessionId, token]);

  // Session timer - countdown from 5 minutes
  useEffect(() => {
    if (isStarted && !isPaused && !isEnding) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Time's up - end session gracefully
            clearInterval(timerRef.current);
            // Wait for TTS to finish if AI is speaking
            const waitForTTS = () => {
              if (isAiSpeakingRef.current) {
                // Check again in 500ms
                setTimeout(waitForTTS, 500);
              } else if (!isEndingRef.current) {
                endSession();
              }
            };
            // Start checking after a brief delay
            setTimeout(waitForTTS, 300);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isStarted, isPaused, isEnding]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate coaching hints based on scenario and conversation
  const getCoachingHints = useCallback(() => {
    if (!scenario) return [];
    
    const baseHints = [
      { icon: '💬', text: 'Use "I feel..." statements instead of "You always..."' },
      { icon: '🎯', text: `Remember your goal: ${scenario.goal?.slice(0, 60)}...` },
      { icon: '👂', text: 'Acknowledge their feelings before sharing yours' },
      { icon: '🤝', text: 'Propose a specific solution or next step' },
      { icon: '😌', text: 'Take a breath if you feel defensive' },
    ];
    
    // Add scenario-specific hints
    if (scenario.category === 'spouse') {
      baseHints.push(
        { icon: '💑', text: 'Frame it as "us vs the problem" not "me vs you"' },
        { icon: '📊', text: 'Suggest looking at the numbers together' }
      );
    } else if (scenario.category === 'kids') {
      baseHints.push(
        { icon: '🧒', text: 'Validate their wants before explaining saving' },
        { icon: '🎮', text: 'Use examples they can relate to' }
      );
    }
    
    // Add hints based on success criteria
    if (scenario.success_criteria) {
      if (scenario.success_criteria.uses_i_statements) {
        baseHints.push({ icon: '✨', text: 'Try: "I need..." or "I want us to..."' });
      }
      if (scenario.success_criteria.proposes_solution) {
        baseHints.push({ icon: '💡', text: 'End with: "What if we tried..."' });
      }
    }
    
    return baseHints;
  }, [scenario]);

  // Rotate hints periodically
  useEffect(() => {
    if (!isStarted || isPaused || !showHints) return;
    
    const hints = getCoachingHints();
    if (hints.length === 0) return;
    
    setCurrentHint(hints[0]);
    
    const interval = setInterval(() => {
      setHintIndex(prev => {
        const nextIndex = (prev + 1) % hints.length;
        setCurrentHint(hints[nextIndex]);
        return nextIndex;
      });
    }, 8000); // Rotate every 8 seconds
    
    return () => clearInterval(interval);
  }, [isStarted, isPaused, showHints, getCoachingHints]);

  // Update hint based on conversation context
  useEffect(() => {
    if (!isStarted || messages.length === 0) return;
    
    const lastAiMessage = [...messages].reverse().find(m => m.role === 'ai');
    if (!lastAiMessage) return;
    
    const content = lastAiMessage.content.toLowerCase();
    
    // Contextual hint selection
    if (content.includes('stressed') || content.includes('worried') || content.includes('anxious')) {
      setCurrentHint({ icon: '💚', text: 'They seem stressed. Acknowledge their feelings first.' });
    } else if (content.includes('why') || content.includes('?')) {
      setCurrentHint({ icon: '🗣️', text: 'They asked a question. Answer directly, then explain.' });
    } else if (content.includes('but') || content.includes("don't")) {
      setCurrentHint({ icon: '🌊', text: 'Stay calm. Find common ground before responding.' });
    }
  }, [messages, isStarted]);

  // Load session without pre-acquiring microphone resources.
  useEffect(() => {
    loadSession();
    
    return () => {
      cleanupMedia();
    };
  }, [sessionId, token]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const requestUserTurn = async ({ recovery = false } = {}) => {
    const state = machineRef.current?.getSnapshot().state;
    if (!isStartedRef.current || isPausedRef.current || isEndingRef.current) return false;
    if (![VOICE_STATES.IDLE, VOICE_STATES.WAITING_FOR_USER, VOICE_STATES.MICROPHONE_READY].includes(state)) {
      console.info('Duplicate microphone request ignored', { state });
      return false;
    }
    const turnId = machineRef.current.beginTurn();
    if (!turnId) return false;
    const snapshot = machineRef.current.getSnapshot();
    const turn = { conversationId: conversationIdRef.current, turnId, turnNumber: snapshot.turnNumber };
    activeTurnRef.current = turn;
    initializeLatencyTurn(turnId, pushToTalkRef.current ? 'push_to_talk' : 'microphone');
    setCurrentTurnId(turnId);
    if (!recovery) recoveryAttemptsRef.current = 0;
    setVoiceError('');
    transitionVoice(VOICE_STATES.REQUEST_MICROPHONE, { recovery, recoveryAttempt: recoveryAttemptsRef.current });
    return startTurn(turn);
  };
  requestUserTurnRef.current = requestUserTurn;

  const startListening = () => requestUserTurn();

  const stopListening = (process = true) => {
    stopTurn(process);
  };

  const cleanupMedia = () => {
    stopListening(false);
    cleanupVoice();
    turnAbortRef.current?.abort();
    turnAbortRef.current = null;
    ttsAbortControllersRef.current.forEach((controller) => controller.abort());
    ttsAbortControllersRef.current.clear();
    orchestrationTimersRef.current.forEach((timer) => clearTimeout(timer));
    orchestrationTimersRef.current.clear();
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
      } catch (e) {
        // Ignore - already closed
      }
    }
    audioContextRef.current = null;
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    flushAudioQueue();
  };

  // Text-to-speech function (single call — used for opening line and fallback)
  const speakText = async (text) => {
    // Don't speak if session is ending
    if (isEndingRef.current) return;
    
    if (!ttsEnabled) {
      transitionVoice(VOICE_STATES.WAITING_FOR_USER, { tts: 'disabled' });
      if (!isEndingRef.current) requestUserTurnRef.current?.();
      return;
    }
    
    // Stop microphone capture while AI is speaking
    stopListening(false);
    setIsAiSpeaking(true);
    isAiSpeakingRef.current = true;
    transitionVoice(VOICE_STATES.GENERATING_TTS, { opening: true, chars: text.length });
    setExternalStage('TTS', 'started', { mode: speedMode ? 'browser' : 'elevenlabs', chars: text.length });

    let hasResumedFromOpening = false;
    const resumeListening = () => {
      if (hasResumedFromOpening) return;
      hasResumedFromOpening = true;
      setIsAiSpeaking(false);
      isAiSpeakingRef.current = false;
      transitionVoice(VOICE_STATES.WAITING_FOR_USER, { opening: true, playback: 'complete' });
      activeTurnRef.current = null;
      // Don't auto-resume in push-to-talk mode — user controls mic
      if (pushToTalkRef.current) return;
      if (isStartedRef.current && !isPausedRef.current && !isEndingRef.current) {
        const timer = setTimeout(() => requestUserTurnRef.current?.(), 300);
        orchestrationTimersRef.current.add(timer);
      }
    };
    
    // Speed Mode: use browser's instant SpeechSynthesis (zero network latency)
    if (speedMode && window.speechSynthesis) {
      window.speechSynthesis.cancel(); // Clear any queued speech
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.05;
      utterance.pitch = 1.0;
      utterance.onend = () => { setExternalStage('PLAYBACK', 'complete'); resumeListening(); };
      utterance.onerror = () => { setExternalStage('PLAYBACK', 'failed'); resumeListening(); };
      setExternalStage('PLAYBACK', 'started', { mode: 'browser' });
      transitionVoice(VOICE_STATES.PLAYING_AUDIO, { mode: 'browser', opening: true });
      window.speechSynthesis.speak(utterance);
      return;
    }
    
    try {
      // Premium Voice: ElevenLabs TTS with browser fallback on error
      const response = await axios.post(`${API}/ai/tts`, {
        text: text,
        voice_id: voiceId
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000
      });
      
      if (isEndingRef.current) { resumeListening(); return; }
      
      const audioData = response.data.audio_base64;
      setExternalStage('TTS', 'complete', { mode: 'elevenlabs' });
      const audioBlob = new Blob(
        [Uint8Array.from(atob(audioData), c => c.charCodeAt(0))],
        { type: 'audio/mpeg' }
      );
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = safariAudioRef.current || new Audio();
      audioPlayerRef.current = audio;
      
      audio.onended = () => { setExternalStage('PLAYBACK', 'complete'); URL.revokeObjectURL(audioUrl); resumeListening(); };
      audio.onerror = () => { setExternalStage('PLAYBACK', 'failed'); URL.revokeObjectURL(audioUrl); resumeListening(); };
      audio.src = audioUrl;
      
      // Resume AudioContext if Safari suspended it
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
      
      await audio.play();
      setExternalStage('PLAYBACK', 'started', { mode: 'elevenlabs' });
      transitionVoice(VOICE_STATES.PLAYING_AUDIO, { mode: 'elevenlabs', opening: true });
      
    } catch (error) {
      console.warn('ElevenLabs TTS failed, using browser voice:', error.message);
      setExternalStage('TTS', 'fallback', { error: error.message });
      if (window.speechSynthesis && !isEndingRef.current) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utterance.onend = () => { setExternalStage('PLAYBACK', 'complete'); resumeListening(); };
        utterance.onerror = () => { setExternalStage('PLAYBACK', 'failed'); resumeListening(); };
        setExternalStage('PLAYBACK', 'started', { mode: 'browser-fallback' });
        transitionVoice(VOICE_STATES.PLAYING_AUDIO, { mode: 'browser-fallback', opening: true });
        window.speechSynthesis.speak(utterance);
      } else {
        resumeListening();
      }
    }
  };

  // === QUEUED TTS PIPELINE (for streaming responses — gapless audio) ===
  
  // Pipeline telemetry
  const pipelineMetrics = useRef({ streamStart: 0, firstTokenAt: 0, chunks: [] });
  const pendingChunksRef = useRef(0); // TTS requests in-flight
  const streamDoneRef = useRef(false); // Has the LLM stream finished?
  
  const logPipeline = (stage, detail = '') => {
    const elapsed = Date.now() - (pipelineMetrics.current.streamStart || Date.now());
    console.log(`[TTS Pipeline +${elapsed}ms] ${stage}${detail ? ': ' + detail : ''}`);
  };

  const generateAudioChunk = async (text, chunkIndex) => {
    const turn = activeTurnRef.current;
    if (!turn) throw new Error('No active conversation turn for TTS.');
    const genStart = Date.now();
    pendingChunksRef.current++;
    logPipeline('TTS_REQUEST', `chunk ${chunkIndex} (${text.length} chars)`);
    markLatencyRef.current?.('tts_started', performance.now(), { sequence: chunkIndex, chars: text.length });
    setExternalStage('TTS', 'started', { chunkIndex, chars: text.length });
    
    if (speedMode && window.speechSynthesis) {
      pendingChunksRef.current--;
      return { type: 'browser', text, index: chunkIndex, sequence: chunkIndex, turnId: turn.turnId, conversationId: turn.conversationId };
    }
    const controller = new AbortController();
    ttsAbortControllersRef.current.add(controller);
    try {
      const checksum = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
        .then((buffer) => [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, '0')).join(''));
      const response = await fetch(`${API}/ai/tts/stream`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
        text,
        voice_id: voiceId,
        conversation_id: turn.conversationId,
        turn_id: turn.turnId,
        sequence: chunkIndex,
        checksum,
        }),
      });
      if (!response.ok) throw new Error(`TTS failed with HTTP ${response.status}.`);
      if (activeTurnRef.current?.turnId !== turn.turnId || response.headers.get('X-Turn-ID') !== turn.turnId || Number(response.headers.get('X-Sequence')) !== chunkIndex) {
        throw new Error('Stale or mismatched TTS chunk discarded.');
      }
      const reader = response.body.getReader();
      const audioParts = [];
      let firstAudioByte = true;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value?.length) {
          if (firstAudioByte) {
            firstAudioByte = false;
            markLatencyRef.current?.('tts_first_byte', performance.now(), { sequence: chunkIndex, bytes: value.length });
            setExternalStage('TTS', 'first_byte', { chunkIndex, latencyMs: Date.now() - genStart });
          }
          audioParts.push(value);
        }
      }
      
      const latency = Date.now() - genStart;
      logPipeline('TTS_READY', `chunk ${chunkIndex} in ${latency}ms`);
      pipelineMetrics.current.chunks.push({ index: chunkIndex, chars: text.length, ttsMs: latency });
      
      const audioBlob = new Blob(audioParts, { type: 'audio/mpeg' });
      pendingChunksRef.current--;
      markLatencyRef.current?.('tts_completed', performance.now(), { sequence: chunkIndex, latencyMs: latency, bytes: audioBlob.size });
      setExternalStage('TTS', 'complete', { chunkIndex, latencyMs: latency });
      return { type: 'elevenlabs', url: URL.createObjectURL(audioBlob), index: chunkIndex, sequence: chunkIndex, text, turnId: turn.turnId, conversationId: turn.conversationId };
    } catch (err) {
      if (controller.signal.aborted) throw err;
      logPipeline('TTS_FALLBACK', `chunk ${chunkIndex} failed: ${err.message}`);
      setExternalStage('TTS', 'fallback', { chunkIndex, error: err.message });
      pendingChunksRef.current--;
      return { type: 'browser', text, index: chunkIndex, sequence: chunkIndex, turnId: turn.turnId, conversationId: turn.conversationId }; // fallback to browser voice
    } finally {
      ttsAbortControllersRef.current.delete(controller);
    }
  };

  const playNextChunk = () => {
    if (isEndingRef.current) {
      isPlayingQueueRef.current = false;
      setIsAiSpeaking(false);
      isAiSpeakingRef.current = false;
      return;
    }
    
    if (audioQueueRef.current.length === 0) {
      if (pendingChunksRef.current > 0 || !streamDoneRef.current) {
        isPlayingQueueRef.current = false;
        return;
      }
      logPipeline('PLAYBACK_COMPLETE', `${pipelineMetrics.current.chunks.length} chunks played`);
      setExternalStage('PLAYBACK', 'complete', { chunks: pipelineMetrics.current.chunks.length });
      markLatencyRef.current?.('playback_completed', performance.now(), { chunks: pipelineMetrics.current.chunks.length });
      setProcessingAcknowledgement('');
      isPlayingQueueRef.current = false;
      setIsAiSpeaking(false);
      isAiSpeakingRef.current = false;
      if (audioPlayerRef.current) {
        audioPlayerRef.current.onended = null;
        audioPlayerRef.current.onerror = null;
        audioPlayerRef.current.pause();
        audioPlayerRef.current.removeAttribute('src');
        audioPlayerRef.current.load();
        audioPlayerRef.current = null;
      }
      transitionVoice(VOICE_STATES.WAITING_FOR_USER, { chunksPlayed: debugSnapshot.chunksPlayed });
      activeTurnRef.current = null;
      if (!pushToTalkRef.current && isStartedRef.current && !isPausedRef.current && !isEndingRef.current) {
        const timer = setTimeout(() => requestUserTurnRef.current?.(), 300);
        orchestrationTimersRef.current.add(timer);
      }
      if (pendingSessionCompleteRef.current) {
        pendingSessionCompleteRef.current = false;
        const timer = setTimeout(() => { if (!isEndingRef.current) endSession(); }, 900);
        orchestrationTimersRef.current.add(timer);
      }
      return;
    }

    const chunk = audioQueueRef.current.shift();
    logPipeline('PLAYBACK_START', `chunk ${chunk.index}, queue depth=${audioQueueRef.current.length}`);
    setExternalStage('PLAYBACK', 'started', { chunkIndex: chunk.index, mode: chunk.type });
    
    if (chunk.type === 'elevenlabs') {
      // Use the pre-unlocked Safari audio element if available, otherwise create new
      const audio = safariAudioRef.current || new Audio();
      audioPlayerRef.current = audio;
      audio.onended = () => {
        logPipeline('PLAYBACK_END', `chunk ${chunk.index}`);
        URL.revokeObjectURL(chunk.url);
        setDebugSnapshot((current) => ({ ...current, chunksPlayed: current.chunksPlayed + 1, chunkOrder: [...current.chunkOrder, chunk.sequence] }));
        playNextChunk();
      };
      audio.onerror = (e) => {
        console.warn(`[TTS] Audio error on chunk ${chunk.index}:`, e);
        URL.revokeObjectURL(chunk.url);
        setExternalStage('PLAYBACK', 'chunk_failed', { chunkIndex: chunk.index });
        playNextChunk();
      };
      audio.src = chunk.url;
      
      // Resume AudioContext if Safari suspended it after mic use
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
      
      audio.play().then(() => {
        logPipeline('PLAY_SUCCESS', `chunk ${chunk.index}`);
        markLatencyRef.current?.('playback_started', performance.now(), { sequence: chunk.sequence, mode: 'elevenlabs' });
        setProcessingAcknowledgement('');
      }).catch((err) => {
        console.warn(`[TTS] play() blocked on chunk ${chunk.index}:`, err.name, err.message,
          `audioCtx=${audioContextRef.current?.state}, paused=${audio.paused}, readyState=${audio.readyState}`);
        URL.revokeObjectURL(chunk.url);
        setExternalStage('PLAYBACK', 'blocked', { chunkIndex: chunk.index, error: err.message });
        // Fallback: use browser speech synthesis with original text
        if (chunk.text && window.speechSynthesis) {
          logPipeline('FALLBACK_BROWSER_TTS', `chunk ${chunk.index} - ${err.name}`);
          window.speechSynthesis.cancel();
          const utterance = new SpeechSynthesisUtterance(chunk.text);
          utterance.rate = 1.05;
          utterance.onend = playNextChunk;
          utterance.onerror = playNextChunk;
          window.speechSynthesis.speak(utterance);
        } else {
          playNextChunk();
        }
      });
    } else {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(chunk.text);
      utterance.rate = 1.05;
      utterance.onend = playNextChunk;
      utterance.onerror = playNextChunk;
      window.speechSynthesis.speak(utterance);
      markLatencyRef.current?.('playback_started', performance.now(), { sequence: chunk.sequence, mode: 'browser' });
      setProcessingAcknowledgement('');
    }
  };

  const enqueueAudioChunk = (audioChunk) => {
    if (isEndingRef.current) return;
    orderedBufferRef.current?.add(audioChunk);
  };

  const enqueueOrderedChunk = (audioChunk) => {
    if (isEndingRef.current || audioChunk.turnId !== activeTurnRef.current?.turnId) {
      audioChunk.url && URL.revokeObjectURL(audioChunk.url);
      return;
    }
    audioQueueRef.current.push(audioChunk);
    setDebugSnapshot((current) => ({ ...current, playbackQueue: audioQueueRef.current.length, chunksReceived: current.chunksReceived + 1 }));
    logPipeline('ENQUEUE', `chunk ${audioChunk.sequence}, queue depth=${audioQueueRef.current.length}`);
    startPlaybackQueue();
  };

  const startPlaybackQueue = () => {
    if (isPlayingQueueRef.current || audioQueueRef.current.length === 0) return;
    isPlayingQueueRef.current = true;
    setIsAiSpeaking(true);
    isAiSpeakingRef.current = true;
    transitionVoice(VOICE_STATES.PLAYING_AUDIO, { firstSequence: audioQueueRef.current[0].sequence, queued: audioQueueRef.current.length });
    playNextChunk();
  };

  if (!orderedBufferRef.current) {
    orderedBufferRef.current = createOrderedChunkBuffer({
      onReady: enqueueOrderedChunk,
      onDuplicate: (chunk) => setExternalStage('TTS', 'duplicate_discarded', { sequence: chunk.sequence }),
      onStale: (chunk) => setExternalStage('TTS', 'stale_discarded', { sequence: chunk.sequence, turnId: chunk.turnId }),
    });
  }

  const flushAudioQueue = () => {
    ttsAbortControllersRef.current.forEach((controller) => controller.abort());
    ttsAbortControllersRef.current.clear();
    window.speechSynthesis?.cancel();
    if (audioPlayerRef.current) {
      const currentSrc = audioPlayerRef.current.src;
      audioPlayerRef.current.pause();
      audioPlayerRef.current.onended = null;
      audioPlayerRef.current.onerror = null;
      audioPlayerRef.current.removeAttribute('src');
      audioPlayerRef.current.load();
      if (currentSrc?.startsWith('blob:')) URL.revokeObjectURL(currentSrc);
      audioPlayerRef.current = null;
    }
    audioQueueRef.current.forEach(c => { if (c.url) URL.revokeObjectURL(c.url); });
    audioQueueRef.current = [];
    isPlayingQueueRef.current = false;
    pendingChunksRef.current = 0;
    streamDoneRef.current = false;
    orderedBufferRef.current?.reset(null);
    setDebugSnapshot((current) => ({ ...current, ttsQueue: 0, playbackQueue: 0, chunksReceived: 0, chunksPlayed: 0, chunkOrder: [] }));
  };

  const loadSession = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSession(response.data);
      
      if (!scenario) {
        const scenarioRes = await axios.get(`${API}/scenarios/${response.data.scenario_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setScenario(scenarioRes.data);
      }
    } catch (error) {
      toast.error('Failed to load session');
      navigate('/scenarios');
    }
  };

  const startPractice = () => {
    setIsStarted(true);
    isStartedRef.current = true;
    if (machineRef.current?.getSnapshot().state === VOICE_STATES.COMPLETE) transitionVoice(VOICE_STATES.IDLE, { reason: 'new_session' });
    startTimeRef.current = Date.now();
    setTimeRemaining(SESSION_DURATION); // Reset timer to 5 minutes
    trackMetaEvent('Lead', { content_name: scenario?.title || 'Practice Session' });
    unlockSafariAudio(); // Unlock audio playback on this user gesture
    resumeVoiceAudio();
    
    // ZERO-LATENCY START: Add opening message INSTANTLY
    if (scenario?.opening_line) {
      const openingMessage = {
        role: 'ai',
        content: scenario.opening_line,
        timestamp: new Date().toISOString()
      };
      
      // Set message IMMEDIATELY - no delay
      setMessages([openingMessage]);
      messagesRef.current = [openingMessage];
      
      // Speak the opening line first; playback completion starts capture.
      if (ttsEnabled) {
        speakText(scenario.opening_line);
      } else if (!useFallbackInput && !pushToTalk) {
        startListening();
      }
    } else {
      // No opening line, start listening immediately (unless push-to-talk)
      if (!pushToTalk) startListening();
    }
  };

  // Push-to-talk handlers
  const pttDown = () => {
    if (isAiSpeakingRef.current || isProcessingRef.current || isEndingRef.current) return;
    unlockSafariAudio(); // Re-unlock on PTT press gesture
    resumeVoiceAudio();
    startListening();
  };

  const pttUp = () => {
    const stopped = stopTurn(true);
    if (stopped) return;
    const state = machineRef.current?.getSnapshot().state;
    if ([VOICE_STATES.REQUEST_MICROPHONE, VOICE_STATES.MICROPHONE_READY, VOICE_STATES.LISTENING].includes(state)) {
      activeTurnRef.current = null;
      cleanupVoice();
      transitionVoice(
        state === VOICE_STATES.REQUEST_MICROPHONE ? VOICE_STATES.IDLE : VOICE_STATES.WAITING_FOR_USER,
        { reason: 'ptt_released_before_recording' },
      );
    }
  };

  const handlePttPointerDown = (event) => {
    event.preventDefault();
    if (pttPointerIdRef.current !== null) return;
    pttPointerIdRef.current = event.pointerId;
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch (error) {
      console.info('Pointer capture unavailable for this PTT gesture', { name: error.name });
    }
    pttDown();
  };

  const handlePttPointerEnd = (event) => {
    event?.preventDefault?.();
    if (event && pttPointerIdRef.current !== null && event.pointerId !== pttPointerIdRef.current) return;
    const pointerId = pttPointerIdRef.current;
    pttPointerIdRef.current = null;
    if (event && pointerId !== null) {
      try {
        if (event.currentTarget.hasPointerCapture?.(pointerId)) event.currentTarget.releasePointerCapture(pointerId);
      } catch (error) {
        console.info('Pointer capture was already released', { name: error.name });
      }
    }
    pttUp();
  };

  const togglePause = () => {
    if (isPaused) {
      setIsPaused(false);
      isPausedRef.current = false;
      if (machineRef.current?.getSnapshot().state === VOICE_STATES.IDLE) transitionVoice(VOICE_STATES.WAITING_FOR_USER, { reason: 'resumed' });
      if (!isAiSpeakingRef.current && !pushToTalk) {
        startListening();
      }
    } else {
      setIsPaused(true);
      isPausedRef.current = true;
      stopListening(false);
      transitionVoice(VOICE_STATES.IDLE, { reason: 'paused' });
    }
  };

  const handleUserSpeech = async (text, sourceTurn = null) => {
    if (!text.trim() || isEndingRef.current) return;
    let turn = sourceTurn;
    if (!turn) {
      const currentState = machineRef.current?.getSnapshot().state;
      const captureStates = [
        VOICE_STATES.REQUEST_MICROPHONE,
        VOICE_STATES.MICROPHONE_READY,
        VOICE_STATES.LISTENING,
        VOICE_STATES.VOICE_DETECTED,
        VOICE_STATES.RECORDING,
      ];
      if (captureStates.includes(currentState)) {
        activeTurnRef.current = null;
        stopTurn(false);
        cleanupVoice();
        transitionVoice(
          currentState === VOICE_STATES.REQUEST_MICROPHONE ? VOICE_STATES.IDLE : VOICE_STATES.WAITING_FOR_USER,
          { reason: 'manual_turn_takeover' },
        );
      }
      const readyState = machineRef.current?.getSnapshot().state;
      if (![VOICE_STATES.IDLE, VOICE_STATES.WAITING_FOR_USER, VOICE_STATES.MICROPHONE_READY].includes(readyState)) return;
      const turnId = machineRef.current.beginTurn();
      if (!turnId) return;
      const snapshot = machineRef.current.getSnapshot();
      turn = { conversationId: conversationIdRef.current, turnId, turnNumber: snapshot.turnNumber };
      activeTurnRef.current = turn;
      initializeLatencyTurn(turnId, 'manual');
      const manualInputAt = performance.now();
      markLatencyRef.current?.('speech_ended', manualInputAt, { source: 'manual', equivalent: 'input_submitted' });
      markLatencyRef.current?.('acknowledged', manualInputAt, { source: 'manual', label: 'Thinking' });
    }
    if (activeTurnRef.current?.turnId !== turn.turnId || isProcessingRef.current) return;
    isProcessingRef.current = true;
    setVoiceError('');
    setProcessingAcknowledgement(sourceTurn ? 'Got it — thinking…' : 'Thinking…');
    keepSafariAudioWarm();
    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setTranscript(prev => prev + `\nUser: ${text}`);
    if (isEndingRef.current) {
      isProcessingRef.current = false;
      return;
    }
    const turnCount = messagesRef.current.filter(m => m.role === 'user').length + 1;
    setIsAiThinking(true);
    markLatencyRef.current?.('llm_started', performance.now(), { chars: text.length });
    transitionVoice(VOICE_STATES.WAITING_FOR_LLM, { turnId: turn.turnId, chars: text.length });
    setExternalStage('LLM', 'started', { chars: text.length });
    const controller = new AbortController();
    turnAbortRef.current?.abort();
    turnAbortRef.current = controller;
    try {
      const response = await fetch(`${API}/ai/roleplay/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: sessionId,
          conversation_id: turn.conversationId,
          turn_id: turn.turnId,
          user_message: text,
          conversation_history: messagesRef.current.map(m => ({
            role: m.role === 'ai' ? 'assistant' : 'user',
            content: m.content
          })),
          turn_count: turnCount,
          time_remaining: timeRemaining,
        }),
        signal: controller.signal,
      });
      if (!response.ok) {
        setExternalStage('LLM', 'failed', { httpStatus: response.status });
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail.detail || `AI request failed with HTTP ${response.status}.`);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      let completion = null;
      let streamError = null;
      const tokenSequences = [];
      const ttsGenerationPromises = [];
      let speechSegmentCount = 0;
      let firstTokenAt = 0;
      flushAudioQueue();
      pipelineMetrics.current = { streamStart: Date.now(), firstTokenAt: 0, chunks: [] };
      orderedBufferRef.current.reset(turn.turnId);
      const parser = createSseParser((event) => {
        if (event.turn_id && event.turn_id !== turn.turnId) { streamError = new Error('A stale AI event was received.'); return; }
        if (event.event === 'token' && event.token) {
          if (!firstTokenAt) {
            firstTokenAt = Date.now();
            pipelineMetrics.current.firstTokenAt = firstTokenAt;
            markLatencyRef.current?.('llm_first_token', performance.now(), { ttftMs: firstTokenAt - pipelineMetrics.current.streamStart });
            setExternalStage('LLM', 'streaming', { ttftMs: firstTokenAt - pipelineMetrics.current.streamStart });
          }
          tokenSequences.push(event.sequence);
          fullText += event.token;
        } else if (event.event === 'speech_segment' && event.text) {
          if (!ttsEnabled) return;
          if (speechSegmentCount === 0) transitionVoice(VOICE_STATES.GENERATING_TTS, { streaming: true, chars: event.text.length });
          speechSegmentCount += 1;
          setDebugSnapshot((current) => ({ ...current, ttsQueue: speechSegmentCount }));
          const task = generateAudioChunk(event.text, event.sequence)
            .then((audioChunk) => enqueueAudioChunk(audioChunk))
            .catch((error) => { streamError = error; });
          ttsGenerationPromises.push(task);
        } else if (event.event === 'complete' || event.done) completion = event;
        else if (event.event === 'error' || event.error) streamError = new Error(event.error || 'AI stream failed.');
      });
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        parser.push(decoder.decode(value, { stream: true }));
        if (streamError || isEndingRef.current || activeTurnRef.current?.turnId !== turn.turnId) break;
      }
      parser.push(decoder.decode());
      parser.flush();
      if (streamError) throw streamError;
      if (controller.signal.aborted || activeTurnRef.current?.turnId !== turn.turnId) throw new Error('The conversation turn was cancelled.');
      await verifyStreamIntegrity({ text: fullText, completion, expectedTurnId: turn.turnId, tokenSequences });
      if (!fullText.trim()) throw new Error('The AI returned an empty completed response.');
      setIsAiThinking(false);
      markLatencyRef.current?.('llm_completed', performance.now(), { chars: fullText.length, serverTiming: completion.server_timing });
      setExternalStage('LLM', 'complete', { chars: fullText.length, checksum: completion.checksum, finishReason: completion.finish_reason });
      setMessages(prev => [...prev, { role: 'ai', content: fullText, timestamp: new Date().toISOString() }]);
      setTranscript(prev => prev + `\nAI: ${fullText}`);
      pendingSessionCompleteRef.current = Boolean(completion.session_complete);
      if (ttsEnabled) {
        streamDoneRef.current = true;
        await Promise.all(ttsGenerationPromises);
        if (streamError) throw streamError;
        if (audioQueueRef.current.length) startPlaybackQueue();
        else if (!isPlayingQueueRef.current) playNextChunk();
      } else {
        transitionVoice(VOICE_STATES.WAITING_FOR_USER, { tts: 'disabled' });
        activeTurnRef.current = null;
        if (pendingSessionCompleteRef.current) {
          pendingSessionCompleteRef.current = false;
          const timer = setTimeout(() => endSession(), 700); orchestrationTimersRef.current.add(timer);
        } else if (!pushToTalkRef.current) {
          const timer = setTimeout(() => requestUserTurnRef.current?.(), 250); orchestrationTimersRef.current.add(timer);
        }
      }
    } catch (error) {
      if (isEndingRef.current) {
        isProcessingRef.current = false;
        return;
      }
      console.error('AI response error:', error);
      setExternalStage('LLM', 'failed', { error: error.message });
      setVoiceError(error.message);
      setProcessingAcknowledgement('');
      toast.error(`The AI turn was discarded: ${error.message}`, { duration: 6000 });
      setIsAiThinking(false);
      flushAudioQueue();
      transitionVoice(VOICE_STATES.WAITING_FOR_USER, { error: error.message });
      activeTurnRef.current = null;
      if (isStartedRef.current && !isPausedRef.current && !isEndingRef.current && !useFallbackInput && !pushToTalkRef.current) {
        const timer = setTimeout(() => requestUserTurnRef.current?.({ recovery: true }), 500);
        orchestrationTimersRef.current.add(timer);
      }
    } finally {
      if (turnAbortRef.current === controller) turnAbortRef.current = null;
      isProcessingRef.current = false;
    }
  };
  handleUserSpeechRef.current = handleUserSpeech;

  // Handle text input submission for browsers without recording support
  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (!textInput.trim() || isProcessingRef.current || isAiSpeakingRef.current) return;
    unlockSafariAudio(); // Re-unlock on text submit gesture
    handleUserSpeech(textInput.trim());
    setTextInput('');
  };

  const endSession = async () => {
    // Immediately mark as ending to stop all processing
    isEndingRef.current = true;
    isProcessingRef.current = true; // Prevent any new processing
    setIsEnding(true);
    setIsStarted(false);
    isStartedRef.current = false;
    transitionVoice(VOICE_STATES.COMPLETE, { reason: 'session_end' });
    turnAbortRef.current?.abort();
    turnAbortRef.current = null;
    orchestrationTimersRef.current.forEach((timer) => clearTimeout(timer));
    orchestrationTimersRef.current.clear();
    
    // IMMEDIATELY stop all media
    // 1. Stop voice capture
    stopListening(false);
    setIsListening(false);
    
    // 2. Stop any playing audio and flush queue
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.src = '';
      audioPlayerRef.current = null;
    }
    flushAudioQueue();
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setIsAiSpeaking(false);
    isAiSpeakingRef.current = false;
    
    // 3. Stop turn-owned microphone resources
    cleanupVoice();
    
    // 4. Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
      } catch (e) {
        // Ignore - already closed
      }
    }
    audioContextRef.current = null;
    
    // Clear video
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    const duration = Math.round((Date.now() - (startTimeRef.current || Date.now())) / 1000);
    
    // Use ref to get latest messages (avoid stale closure)
    const currentMessages = messagesRef.current;
    
    // Build full transcript
    const fullTranscript = currentMessages
      .map(m => `${m.role === 'user' ? 'User' : 'AI Partner'}: ${m.content}`)
      .join('\n');
    
    const aiTranscript = currentMessages
      .filter(m => m.role === 'ai')
      .map(m => m.content)
      .join('\n');
    
    // Check if there's actual conversation content
    if (currentMessages.length <= 1) {
      toast.error('Session ended with no conversation. Try speaking during the session.');
      setIsEnding(false);
      isEndingRef.current = false;
      isProcessingRef.current = false;
      navigate('/scenarios');
      return;
    }
    
    try {
      // Score the session
      const scoreResponse = await axios.post(`${API}/ai/score`, {
        session_id: sessionId,
        full_transcript: fullTranscript,
        ai_transcript: aiTranscript
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update session with scores and next_steps
      await axios.put(`${API}/sessions/${sessionId}`, {
        transcript: fullTranscript,
        ai_transcript: aiTranscript,
        ended_at: new Date().toISOString(),
        duration_seconds: duration,
        overall_score: scoreResponse.data.overall_score,
        scores: scoreResponse.data.scores,
        signals: scoreResponse.data.signals,
        feedback_summary: scoreResponse.data.feedback_summary,
        coaching_tips: scoreResponse.data.coaching_tips,
        next_steps: scoreResponse.data.next_steps,
        next_recommendation: scoreResponse.data.next_recommendation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      navigate(`/feedback/${sessionId}`);
    } catch (error) {
      console.error('End session error:', error);
      // Resolution Engine: Provide suggested fix, not just error
      toast.error('Unable to save session. Your conversation was recorded - try refreshing to see your feedback.', { duration: 8000 });
      setIsEnding(false);
    }
  };

  const toggleVideo = () => setVideoEnabled((value) => !value);

  // Quick reply handler
  const handleQuickReply = (text) => {
    if (isAiThinking || isTranscribing) return;
    unlockSafariAudio(); // Re-unlock on every user gesture
    if (isAiSpeakingRef.current) {
      window.speechSynthesis?.cancel();
      if (audioPlayerRef.current) {
        audioPlayerRef.current.onended = null;
        audioPlayerRef.current.onerror = null;
        audioPlayerRef.current.pause();
        audioPlayerRef.current.removeAttribute('src');
        audioPlayerRef.current.load();
        audioPlayerRef.current = null;
      }
      flushAudioQueue();
      setIsAiSpeaking(false);
      isAiSpeakingRef.current = false;
      activeTurnRef.current = null;
      transitionVoice(VOICE_STATES.WAITING_FOR_USER, { reason: 'user_barge_in', input: 'quick_reply' });
      setExternalStage('PLAYBACK', 'interrupted_by_user', { input: 'quick_reply' });
    }
    handleUserSpeech(text);
  };

  const progressPercent = Math.round(((SESSION_DURATION - timeRemaining) / SESSION_DURATION) * 100);

  const quickReplies = [
    "Tell me more",
    "I'm not sure",
    "That makes sense",
    "Can you explain that?",
    "What do you recommend?",
    "I'm worried about that"
  ];

  // Financial journey steps
  const journeySteps = [
    { label: 'Income', done: progressPercent >= 15 },
    { label: 'Budget', done: progressPercent >= 30 },
    { label: 'Debt', done: progressPercent >= 50 },
    { label: 'Savings', done: progressPercent >= 65 },
    { label: 'Investing', done: progressPercent >= 80 },
    { label: 'Retirement', done: progressPercent >= 95 },
  ];
  const activeStep = journeySteps.findIndex(s => !s.done);

  const advisorStatus = isAiSpeaking ? 'Speaking' : isAiThinking ? 'Thinking' : isTranscribing ? 'Transcribing' : isListening ? 'Listening' : 'Available';
  const advisorStatusColor = isAiSpeaking ? '#14B8A6' : isAiThinking ? '#8B5CF6' : isListening ? '#F59E0B' : '#22C55E';

  return (
    <div className="h-screen flex flex-col relative" style={{ background: '#FAF8F5' }} data-testid="practice-room">

      {/* Scoring Overlay */}
      {isEnding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: '#FAF8F5' }} data-testid="scoring-overlay">
          <div className="text-center space-y-8 px-6 max-w-md">
            <div className="relative w-32 h-32 mx-auto">
              <svg className="w-32 h-32 animate-spin" style={{ animationDuration: '3s' }} viewBox="0 0 128 128">
                <circle cx="64" cy="64" r="56" fill="none" stroke="rgba(107,143,113,0.15)" strokeWidth="4" />
                <circle cx="64" cy="64" r="56" fill="none" stroke="#6B8F71" strokeWidth="4" strokeDasharray="352" strokeDashoffset="264" strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <img src={advisorImage} alt="" className="w-[72px] h-[72px] rounded-full object-cover" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
              </div>
            </div>
            <div className="space-y-3">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: '#2D2A26' }}>{voiceName} is preparing your report</h2>
              <p className="text-base" style={{ color: '#6B6560' }}>Analyzing confidence, clarity, tone, and effectiveness...</p>
            </div>
            <div className="flex justify-center gap-2">
              {[0, 1, 2].map(i => (
                <span key={i} className="w-2.5 h-2.5 rounded-full animate-bounce" style={{ backgroundColor: '#6B8F71', animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TOP BAR */}
      <header className="relative z-10 px-4 lg:px-6 py-3 flex items-center justify-between flex-shrink-0" style={{ background: 'rgba(250,248,245,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (isStarted) {
                setConfirmModal({ title: 'Leave session?', message: 'Your progress has been saved. You can start a new session anytime.', onConfirm: () => navigate('/scenarios') });
              } else {
                navigate('/scenarios');
              }
            }}
            className="p-1.5 rounded-full transition-colors hover:bg-black/5"
            style={{ color: '#6B6560' }}
            data-testid="back-to-scenarios-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <img src={advisorImage} alt={voiceName} className="w-8 h-8 rounded-full object-cover lg:hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }} />
          <div className="hidden lg:block">
            <p className="text-sm font-medium" style={{ color: '#2D2A26' }}>{scenario?.title || 'Financial Coaching Session'}</p>
          </div>
          <div className="lg:hidden">
            <p className="text-white text-sm font-medium">{voiceName}</p>
            <p className="text-xs" style={{ color: advisorStatusColor }}>{advisorStatus}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isStarted && (
            <>
              <div className="hidden sm:flex items-center gap-2">
                <div className="w-20 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.06)' }}>
                  <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${progressPercent}%`, background: '#6B8F71' }} />
                </div>
                <span className="text-xs font-mono" style={{ color: '#A39E98' }}>{progressPercent}%</span>
              </div>
              <div className={`text-xs font-mono px-2.5 py-1 rounded-full flex items-center gap-1.5 ${
                timeRemaining <= 30 ? 'bg-red-50 text-red-600' : timeRemaining <= 60 ? 'bg-amber-50 text-amber-700' : ''
              }`} style={timeRemaining > 60 ? { background: 'rgba(0,0,0,0.04)', color: '#6B6560' } : {}} data-testid="session-timer">
                <Clock className="w-3 h-3" />
                {formatTime(timeRemaining)}
              </div>
            </>
          )}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowVoiceDiagnostics(true)}
              className={`p-1.5 rounded-full transition-colors ${voiceError ? 'text-[#C4613A]' : 'text-[#A39E98]'}`}
              title="Voice diagnostics"
              aria-label="Open voice diagnostics"
              data-testid="voice-diagnostics-button"
            >
              <Activity className="w-4 h-4" />
            </button>
            <button onClick={() => setTtsEnabled(!ttsEnabled)} className={`p-1.5 rounded-full transition-colors ${ttsEnabled ? 'text-[#6B8F71]' : 'text-[#A39E98]'}`} data-testid="toggle-tts-btn">
              {ttsEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button onClick={() => { setSpeedMode(!speedMode); if (!speedMode) setTtsEnabled(true); }} className={`p-1.5 rounded-full transition-colors ${speedMode ? 'text-[#C4613A]' : 'text-[#A39E98]'}`} data-testid="toggle-speed-mode-btn">
              <Zap className={`w-4 h-4 ${speedMode ? 'fill-current' : ''}`} />
            </button>
            {!useFallbackInput && (
              <button onClick={() => { setPushToTalk(!pushToTalk); if (!pushToTalk) stopListening(false); }} className={`p-1.5 rounded-full transition-colors ${pushToTalk ? 'text-[#C4613A]' : 'text-[#A39E98]'}`} title={pushToTalk ? 'Switch to continuous listening' : 'Switch to push-to-talk'} data-testid="toggle-ptt-btn">
                <Hand className={`w-4 h-4 ${pushToTalk ? 'fill-current' : ''}`} />
              </button>
            )}
            {autoSaved && (
              <span className="text-[10px] flex items-center gap-1 px-2 animate-fade-in" style={{ color: '#6B8F71' }} data-testid="auto-saved-indicator">
                <Save className="w-3 h-3" /> Saved
              </span>
            )}
          </div>
          {isStarted && (
            <button onClick={() => setConfirmModal({ title: 'End this session?', message: 'Your conversation will be scored and you\'ll receive feedback on your communication skills.', onConfirm: () => { setConfirmModal(null); endSession(); } })}
              className="text-xs px-3 py-1.5 rounded-full transition-colors" style={{ background: 'rgba(196,97,58,0.08)', color: '#C4613A' }} data-testid="end-session-btn">End</button>
          )}
        </div>
      </header>

      {/* CONVERSATION-LEVEL DISCLOSURE BANNER */}
      {isStarted && (
        <div className="px-4 py-1.5 text-center flex-shrink-0" style={{ background: 'rgba(107,143,113,0.06)', borderBottom: '1px solid rgba(107,143,113,0.1)' }} data-testid="session-disclosure-banner">
          <p className="text-[11px] tracking-wide" style={{ color: '#8B7355', fontFamily: "'DM Sans', sans-serif" }}>
            Educational Practice Session &nbsp;&bull;&nbsp; Not Financial Advice
          </p>
        </div>
      )}

      {/* MAIN TWO-PANEL LAYOUT */}
      <div className="flex-1 flex overflow-hidden">

        {/* LEFT: Conversation */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto" style={{ scrollBehavior: 'smooth' }}>
            <div className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12 py-8 space-y-6 pb-52">

              {/* Pre-start */}
              {!isStarted ? (
                !complianceAccepted ? (
                  /* === COMPLIANCE DISCLOSURE SCREEN === */
                  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="compliance-screen">
                    <div className="w-full max-w-xl space-y-6">
                      <div className="space-y-2">
                        <p className="text-xs font-semibold tracking-[0.14em] uppercase" style={{ color: '#8B7355' }}>Before You Begin</p>
                        <h2 className="text-2xl sm:text-3xl font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: '#2D2A26' }}>A Quick Note</h2>
                      </div>

                      <p className="text-base leading-relaxed" style={{ color: '#5A5040' }}>
                        This platform provides educational financial coaching and conversation practice. The AI coach helps you explore financial topics, improve communication skills, and think through financial decisions.
                      </p>
                      <p className="text-sm leading-relaxed" style={{ color: '#8B7355' }}>
                        The information provided is for educational purposes only and should not be considered financial, investment, tax, legal, or professional advice.
                      </p>

                      <div className="rounded-2xl p-5 text-left space-y-3" style={{ background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
                        <p className="text-xs font-semibold tracking-wider" style={{ color: '#6B8F71' }}>IMPORTANT DISCLOSURES</p>
                        <ul className="space-y-2.5">
                          {[
                            'Conversations are designed for education, coaching, and practice.',
                            'AI-generated responses are not personalized financial advice.',
                            'The AI does not act as a licensed financial advisor, tax professional, attorney, or fiduciary.',
                            'Financial decisions should be evaluated with qualified professionals when appropriate.',
                            'Information provided may be incomplete, outdated, or not applicable to your personal situation.',
                            'Use of this platform does not create an advisor-client relationship.',
                          ].map((item, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed" style={{ color: '#5A5040' }}>
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#6B8F71' }} />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <label className="flex items-start gap-3 text-left cursor-pointer rounded-xl p-4" style={{ background: complianceChecked ? 'rgba(107,143,113,0.06)' : 'transparent', border: complianceChecked ? '1px solid rgba(107,143,113,0.2)' : '1px solid rgba(0,0,0,0.08)', transition: 'all 0.2s ease' }} data-testid="compliance-checkbox-label">
                        <input
                          type="checkbox"
                          checked={complianceChecked}
                          onChange={(e) => setComplianceChecked(e.target.checked)}
                          className="mt-0.5 w-4 h-4 rounded accent-[#6B8F71]"
                          style={{ accentColor: '#6B8F71' }}
                          data-testid="compliance-checkbox"
                        />
                        <span className="text-sm leading-relaxed" style={{ color: '#2D2A26' }}>
                          I understand that this platform is for educational and practice purposes only and that AI-generated responses do not constitute financial, investment, tax, or legal advice.
                        </span>
                      </label>

                      <Button
                        onClick={() => setComplianceAccepted(true)}
                        disabled={!complianceChecked}
                        className="h-13 px-8 text-white rounded-full text-base font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: complianceChecked ? '#6B8F71' : '#A39E98', boxShadow: complianceChecked ? '0 4px 20px rgba(107,143,113,0.25)' : 'none' }}
                        data-testid="compliance-continue-btn"
                      >
                        Continue to Session
                      </Button>
                    </div>
                  </div>
                ) : (
                /* === ADVISOR INTRO SCREEN === */
                <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-8">
                  <div className="relative">
                    <img src={advisorImage} alt={voiceName} className="w-36 h-36 rounded-full object-cover" style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.12)', border: '4px solid #FFFFFF' }} />
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full border-4 flex items-center justify-center" style={{ borderColor: '#FAF8F5', background: '#6B8F71' }}>
                      <Check className="w-3.5 h-3.5 text-white" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-3xl font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: '#2D2A26' }}>Meet {voiceName}</h2>
                    <p className="text-lg font-medium" style={{ color: '#6B8F71' }}>Certified Financial Coach</p>
                    <p className="text-base max-w-md leading-relaxed" style={{ color: '#6B6560' }}>{scenario?.goal}</p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-3">
                    {['Personalized Guidance', 'Private & Secure', 'CFP Framework'].map((t, i) => (
                      <span key={i} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm" style={{ background: '#FFFFFF', color: '#6B6560', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
                        <Shield className="w-3.5 h-3.5" style={{ color: '#6B8F71' }} />{t}
                      </span>
                    ))}
                  </div>
                  {scenario?.opening_line && (
                    <div className="w-full max-w-lg rounded-2xl p-6" style={{ background: '#FFFFFF', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
                      <p className="text-xs font-semibold tracking-wider mb-2" style={{ color: '#A39E98' }}>{voiceName.toUpperCase()} WILL SAY</p>
                      <p className="text-base leading-relaxed italic" style={{ fontFamily: "'Playfair Display', serif", color: '#2D2A26' }}>&ldquo;{scenario.opening_line.slice(0, 100)}...&rdquo;</p>
                    </div>
                  )}
                  <Button onClick={startPractice} disabled={!scenario?.opening_line}
                    className="h-14 px-10 text-white rounded-full text-base font-semibold transition-all disabled:opacity-50"
                    style={{ background: '#6B8F71', boxShadow: '0 4px 20px rgba(107,143,113,0.25)' }}
                    data-testid="start-practice-btn">
                    {scenario?.opening_line ? <><Play className="w-5 h-5 mr-2" /> Begin Session</> : <><span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> Loading...</>}
                  </Button>
                </div>
                )
              ) : (
                <>
                  {/* Coaching Insight */}
                  {showHints && currentHint && !isPaused && (
                    <div className="animate-message-in rounded-2xl p-5" style={{ background: '#FFFFFF', border: '1px solid rgba(196,97,58,0.15)', boxShadow: '0 2px 16px rgba(196,97,58,0.06)' }} data-testid="coaching-hint">
                      <div className="flex items-start gap-3.5">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(196,97,58,0.08)' }}>
                          <Lightbulb className="w-4 h-4" style={{ color: '#C4613A' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold tracking-wider mb-1.5" style={{ color: '#C4613A' }}>COACHING INSIGHT</p>
                          <p className="text-base leading-relaxed" style={{ color: '#2D2A26' }}>{currentHint.text}</p>
                        </div>
                        <button onClick={() => { const h = getCoachingHints(); const n = (hintIndex + 1) % h.length; setHintIndex(n); setCurrentHint(h[n]); }}
                          className="p-1.5 rounded-lg transition-colors" style={{ color: '#A39E98' }}><ChevronRight className="w-4 h-4" /></button>
                      </div>
                    </div>
                  )}

                  {/* Messages */}
                  {messages.map((msg, i) => (
                    <div key={i} className="animate-message-in" style={{ animationDelay: `${Math.min(i * 0.05, 0.3)}s` }}>
                      {msg.role === 'ai' ? (
                        <div className="flex items-start gap-3.5">
                          <img src={advisorImage} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0 mt-1" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: isAiSpeaking && i === messages.length - 1 ? '2px solid #6B8F71' : '2px solid transparent' }} />
                          <div className="flex-1 rounded-2xl p-5" style={{ background: '#FFFFFF', boxShadow: '0 1px 8px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.06)' }}>
                            <p className="text-[17px] leading-[1.8]" style={{ color: '#2D2A26' }}>{msg.content}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-end pl-16">
                          <div className="rounded-2xl p-5 px-6" style={{ background: '#6B8F71', boxShadow: '0 2px 12px rgba(107,143,113,0.2)' }}>
                            <p className="text-[17px] leading-[1.8] text-white">{msg.content}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* AI Thinking */}
                  {isAiThinking && (
                    <div className="animate-message-in flex items-start gap-3.5">
                      <img src={advisorImage} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0 mt-1" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '2px solid #6B8F71' }} />
                      <div className="rounded-2xl p-5" style={{ background: '#FFFFFF', boxShadow: '0 1px 8px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.06)' }}>
                        <div className="flex items-center gap-2">
                          {[0, 1, 2].map(i => (
                            <span key={i} className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#6B8F71', animation: `thinkDot 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quick Replies */}
                  {isStarted && !isAiThinking && !isPaused && !isEnding && messages.length > 0 && messages[messages.length - 1]?.role === 'ai' && (
                    <div className="flex flex-wrap gap-2.5 pl-14 animate-message-in" style={{ animationDelay: '0.2s' }}>
                      {quickReplies.map((reply, i) => (
                        <button key={i} onClick={() => handleQuickReply(reply)} disabled={isAiThinking || isTranscribing}
                          className="px-4 py-2 rounded-full text-sm transition-all disabled:opacity-30"
                          style={{ background: '#FFFFFF', color: '#2D2A26', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(107,143,113,0.06)'; e.currentTarget.style.borderColor = 'rgba(107,143,113,0.2)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)'; }}
                          data-testid={`quick-reply-${i}`}>{reply}</button>
                      ))}
                    </div>
                  )}

                  {/* Pause */}
                  {isPaused && (
                    <div className="rounded-2xl p-6 text-center space-y-4" style={{ background: '#FFFFFF', border: '1px solid rgba(196,97,58,0.12)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                      <p className="font-medium text-base" style={{ color: '#C4613A' }}>Session Paused</p>
                      <div className="flex gap-3 justify-center">
                        <Button onClick={togglePause} className="text-white rounded-full px-5 text-sm" style={{ background: '#6B8F71' }}><Play className="w-4 h-4 mr-1.5" />Continue</Button>
                        <Button variant="outline" onClick={() => { setMessages(messages.slice(0, -2)); togglePause(); }} className="rounded-full px-5 text-sm" style={{ border: '1px solid rgba(0,0,0,0.1)', color: '#6B6560' }}><RefreshCw className="w-4 h-4 mr-1.5" />Retry</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* BOTTOM CONTROLS */}
          {isStarted && !isPaused && !isEnding && (
            <div className="fixed bottom-0 left-0 right-0 lg:right-[24%] pb-6 pt-20 pointer-events-none" style={{ background: 'linear-gradient(to top, #FAF8F5 55%, transparent)' }}>
              {(voiceError || processingAcknowledgement || isTranscribing) && (
                <div className="max-w-md mx-auto mb-3 px-4 pointer-events-auto">
                  <div className="rounded-xl px-4 py-2 text-center" style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: voiceError ? '1px solid rgba(196,97,58,0.22)' : '1px solid rgba(107,143,113,0.15)' }} data-testid="voice-capture-status">
                    <p className="text-sm" style={{ color: voiceError ? '#9A452A' : '#2D2A26' }}>{voiceError || processingAcknowledgement || 'Transcribing your response…'}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-center gap-4 pointer-events-auto">
                <button onClick={togglePause} disabled={isAiSpeaking}
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-all disabled:opacity-30" style={{ background: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)', color: '#6B6560' }} data-testid="pause-btn">
                  <Pause className="w-4 h-4" />
                </button>
                {!useFallbackInput ? (
                  pushToTalk ? (
                    <button
                      onPointerDown={handlePttPointerDown}
                      onPointerUp={handlePttPointerEnd}
                      onPointerCancel={handlePttPointerEnd}
                      onLostPointerCapture={() => {
                        if (pttPointerIdRef.current !== null) handlePttPointerEnd();
                      }}
                      disabled={isAiSpeaking || isAiThinking || isTranscribing || isProcessingRef.current}
                      className={`w-[72px] h-[72px] rounded-full flex items-center justify-center transition-all disabled:opacity-30 select-none ${
                        isListening ? 'scale-110' : ''
                      }`} style={{
                        background: isListening ? '#C4613A' : '#FFFFFF',
                        boxShadow: isListening ? '0 8px 30px rgba(196,97,58,0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
                        border: isListening ? 'none' : '2px solid rgba(196,97,58,0.2)',
                        touchAction: 'none',
                      }} data-testid="ptt-btn">
                      {isListening ? (
                        <div className="flex items-center gap-[3px]">
                          {[0,1,2,3,4].map(i => (<span key={i} className="w-[3px] bg-white rounded-full" style={{ height: `${8 + Math.random()*14}px`, animation: `waveBar 0.6s ease-in-out ${i*0.1}s infinite` }} />))}
                        </div>
                      ) : <Hand className="w-7 h-7" style={{ color: '#C4613A' }} />}
                    </button>
                  ) : (
                  <button onClick={() => { unlockSafariAudio(); if (isListening) stopListening(true); else startListening(); }}
                    disabled={isAiSpeaking || isAiThinking || isTranscribing || isProcessingRef.current}
                    className={`w-[72px] h-[72px] rounded-full flex items-center justify-center transition-all disabled:opacity-30 ${
                      isListening ? 'animate-mic-pulse' : ''
                    }`} style={{
                      background: isListening ? '#6B8F71' : '#FFFFFF',
                      boxShadow: isListening ? '0 8px 30px rgba(107,143,113,0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
                      border: isListening ? 'none' : '2px solid rgba(107,143,113,0.2)',
                    }} data-testid="mic-btn">
                    {isListening ? (
                      <div className="flex items-center gap-[3px]">
                        {[0,1,2,3,4].map(i => (<span key={i} className="w-[3px] bg-white rounded-full" style={{ height: `${8 + Math.random()*14}px`, animation: `waveBar 0.6s ease-in-out ${i*0.1}s infinite` }} />))}
                      </div>
                    ) : <Mic className="w-7 h-7" style={{ color: '#6B8F71' }} />}
                  </button>
                  )
                ) : (
                  <form onSubmit={handleTextSubmit} className="flex items-center gap-2 max-w-md w-full">
                    <input type="text" value={textInput} onChange={(e) => setTextInput(e.target.value)}
                      placeholder="Type your response..." disabled={isAiSpeaking || isAiThinking || isProcessingRef.current}
                      className="flex-1 px-5 py-3 rounded-full text-sm focus:outline-none focus:ring-2 disabled:opacity-30"
                      style={{ background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.08)', color: '#2D2A26', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                      data-testid="fallback-text-input" />
                    <button type="submit" disabled={!textInput.trim() || isAiSpeaking || isAiThinking}
                      className="w-11 h-11 rounded-full flex items-center justify-center text-white disabled:opacity-30" style={{ background: '#6B8F71' }} data-testid="fallback-send-btn">
                      <Send className="w-4 h-4" />
                    </button>
                  </form>
                )}
                <button onClick={toggleVideo}
                  className={`w-11 h-11 rounded-full flex items-center justify-center transition-all`}
                  style={{ background: videoEnabled ? '#FFFFFF' : 'rgba(196,97,58,0.08)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: videoEnabled ? '1px solid rgba(0,0,0,0.06)' : '1px solid rgba(196,97,58,0.15)', color: videoEnabled ? '#6B6560' : '#C4613A' }}
                  data-testid="toggle-video-btn">
                  {videoEnabled ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
                </button>
              </div>
              {isListening && !pushToTalk && (
                <div className="max-w-xs mx-auto mt-2.5 px-4"><div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.06)' }}><div className="h-full rounded-full transition-all duration-75" style={{ width: `${micLevel}%`, background: '#6B8F71' }} /></div></div>
              )}
              {pushToTalk && !isListening && (
                <p className="text-center text-[11px] mt-2.5" style={{ color: '#A39E98' }}>Hold to speak</p>
              )}
              {!pushToTalk && !isListening && !isTranscribing && !voiceError && (
                <p className="text-center text-[11px] mt-2.5" style={{ color: '#A39E98' }}>Tap to speak</p>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Advisor Panel — Desktop only */}
        <div className="hidden lg:flex w-[24%] max-w-[320px] flex-col flex-shrink-0" style={{ background: '#FFFFFF', borderLeft: '1px solid rgba(0,0,0,0.06)' }}>
          <div className="flex flex-col items-center pt-10 pb-8 px-5">
            <div className="relative mb-5">
              <img src={advisorImage} alt={voiceName} className="w-36 h-36 rounded-full object-cover" style={{
                boxShadow: isAiSpeaking ? '0 0 0 4px rgba(107,143,113,0.3), 0 8px 30px rgba(0,0,0,0.12)' : '0 8px 30px rgba(0,0,0,0.1)',
                border: '4px solid #FFFFFF',
                transition: 'box-shadow 0.5s ease',
              }} />
              <div className="absolute bottom-2 right-2 w-6 h-6 rounded-full border-[3px] flex items-center justify-center" style={{ borderColor: '#FFFFFF', backgroundColor: advisorStatusColor === '#14B8A6' ? '#6B8F71' : advisorStatusColor === '#F59E0B' ? '#C4613A' : advisorStatusColor }} />
            </div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: '#2D2A26' }}>{voiceName}</h3>
            <p className="text-sm font-medium mt-1" style={{ color: '#6B8F71' }}>Certified Financial Coach</p>

            {/* Waveform visualization */}
            {isAiSpeaking && (
              <div className="flex items-end gap-[3px] mt-4 h-8">
                {[0,1,2,3,4,5,6,7,8].map(i => (
                  <span key={i} className="w-[3px] rounded-full" style={{ backgroundColor: '#6B8F71', animation: `waveBar 0.7s ease-in-out ${i*0.07}s infinite` }} />
                ))}
              </div>
            )}

            <div className="mt-4 px-4 py-1.5 rounded-full text-xs font-medium flex items-center gap-2" style={{
              background: advisorStatusColor === '#14B8A6' ? 'rgba(107,143,113,0.08)' : advisorStatusColor === '#F59E0B' ? 'rgba(196,97,58,0.08)' : `${advisorStatusColor}10`,
              color: advisorStatusColor === '#14B8A6' ? '#6B8F71' : advisorStatusColor === '#F59E0B' ? '#C4613A' : advisorStatusColor,
              border: `1px solid ${advisorStatusColor === '#14B8A6' ? 'rgba(107,143,113,0.15)' : advisorStatusColor === '#F59E0B' ? 'rgba(196,97,58,0.15)' : advisorStatusColor + '20'}`,
            }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: advisorStatusColor === '#14B8A6' ? '#6B8F71' : advisorStatusColor === '#F59E0B' ? '#C4613A' : advisorStatusColor }} />
              {advisorStatus}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 space-y-4 pb-6">
            <div className="rounded-xl p-4" style={{ background: '#FAF8F5', border: '1px solid rgba(0,0,0,0.04)' }}>
              <p className="text-xs font-semibold tracking-wider mb-3" style={{ color: '#A39E98' }}>SPECIALIZES IN</p>
              <div className="flex flex-wrap gap-1.5">
                {['Budgeting', 'Investing', 'Debt Management', 'Retirement'].map((s, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full" style={{ background: '#FFFFFF', color: '#6B6560', border: '1px solid rgba(0,0,0,0.06)' }}>{s}</span>
                ))}
              </div>
            </div>

            {isStarted && (
              <div className="rounded-xl p-4" style={{ background: '#FAF8F5', border: '1px solid rgba(0,0,0,0.04)' }}>
                <p className="text-xs font-semibold tracking-wider mb-3" style={{ color: '#A39E98' }}>FINANCIAL JOURNEY</p>
                <div className="space-y-3">
                  {journeySteps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0`} style={{
                        background: step.done ? '#6B8F71' : i === activeStep ? 'rgba(107,143,113,0.1)' : '#F0EDEA',
                        border: i === activeStep && !step.done ? '2px solid #6B8F71' : 'none',
                      }}>
                        {step.done ? <Check className="w-3 h-3 text-white" /> : i === activeStep ? <span className="w-2 h-2 rounded-full" style={{ background: '#6B8F71' }} /> : null}
                      </div>
                      <span className="text-sm" style={{ color: step.done ? '#6B8F71' : i === activeStep ? '#2D2A26' : '#A39E98', fontWeight: i === activeStep ? 500 : 400 }}>{step.label}</span>
                      {i === activeStep && <span className="ml-auto text-xs" style={{ color: '#6B8F71' }}>Current</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isStarted && scenario?.goal && (
              <div className="rounded-xl p-4" style={{ background: 'rgba(107,143,113,0.04)', border: '1px solid rgba(107,143,113,0.1)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4" style={{ color: '#6B8F71' }} />
                  <p className="text-xs font-semibold tracking-wider" style={{ color: '#6B8F71' }}>SESSION GOAL</p>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: '#2D2A26' }}>{scenario.goal}</p>
              </div>
            )}

            <div className="space-y-2.5 pt-2">
              {[
                { icon: Shield, text: 'Private & Secure Session' },
                { icon: Check, text: 'CFP-Inspired Framework' },
                { icon: TrendingUp, text: 'Personalized Guidance' },
              ].map((trust, i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm" style={{ color: '#A39E98' }}>
                  <trust.icon className="w-3.5 h-3.5" style={{ color: '#6B8F71' }} />
                  <span>{trust.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: Advisor sticky bar */}
      {isStarted && !isEnding && (
        <div className="lg:hidden fixed top-[53px] left-0 right-0 z-10 px-4 py-2.5 flex items-center gap-3" style={{ background: 'rgba(250,248,245,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <div className="relative flex-shrink-0">
            <img src={advisorImage} alt="" className="w-10 h-10 rounded-full object-cover" style={{ boxShadow: isAiSpeaking ? '0 0 0 3px rgba(107,143,113,0.3)' : '0 2px 8px rgba(0,0,0,0.08)', border: '2px solid #FFFFFF', transition: 'box-shadow 0.3s ease' }} />
            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2" style={{ borderColor: '#FAF8F5', backgroundColor: advisorStatusColor === '#14B8A6' ? '#6B8F71' : advisorStatusColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium" style={{ color: '#2D2A26' }}>{voiceName}</p>
            <p className="text-xs" style={{ color: '#6B8F71' }}>{advisorStatus}</p>
          </div>
          {isAiSpeaking && (
            <div className="flex items-center gap-[2px]">
              {[0,1,2,3].map(i => (<span key={i} className="w-[2px] rounded-full" style={{ backgroundColor: '#6B8F71', height: '10px', animation: `waveBar 0.7s ease-in-out ${i*0.1}s infinite` }} />))}
            </div>
          )}
          <div className="sm:hidden flex items-center gap-1.5">
            <div className="w-14 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.06)' }}><div className="h-full rounded-full" style={{ width: `${progressPercent}%`, background: '#6B8F71' }} /></div>
          </div>
        </div>
      )}

      <video ref={videoRef} autoPlay playsInline muted className="hidden" />

      <VoiceDiagnosticsPanel
        open={showVoiceDiagnostics}
        onClose={() => setShowVoiceDiagnostics(false)}
        diagnostics={voiceDiagnostics}
        environment={voiceEnvironment}
        events={voiceEvents}
        features={voiceFeatures}
        orchestration={{
          conversationId: conversationIdRef.current,
          turnId: currentTurnId,
          turnNumber: machineRef.current?.getSnapshot().turnNumber,
          state: conversationState,
          recoveryAttempts: recoveryAttemptsRef.current,
          ...debugSnapshot,
        }}
        onRetry={async () => {
          setVoiceError('');
          await cleanupVoice();
          if (isStartedRef.current && !isAiSpeakingRef.current && !isProcessingRef.current) startListening();
        }}
      />

      {/* IN-APP CONFIRM MODAL */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="confirm-modal-overlay">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setConfirmModal(null)} />
          <div className="relative w-full max-w-sm mx-4 rounded-2xl p-6 space-y-4" style={{ background: '#FAF8F5', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }} data-testid="confirm-modal">
            <h3 className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: '#1A2E1A' }}>{confirmModal.title}</h3>
            <p className="text-sm leading-relaxed" style={{ color: '#5A5040' }}>{confirmModal.message}</p>
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 py-2.5 rounded-full text-sm font-medium transition-colors"
                style={{ border: '1px solid #D4C9B8', color: '#5A4A35', background: '#fff' }}
                data-testid="confirm-modal-cancel"
              >
                Keep Going
              </button>
              <button
                onClick={() => { const cb = confirmModal.onConfirm; setConfirmModal(null); cb(); }}
                className="flex-1 py-2.5 rounded-full text-sm font-medium transition-colors"
                style={{ background: '#C4613A', color: '#fff' }}
                data-testid="confirm-modal-confirm"
              >
                End Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
